import { Container, Graphics, InteractionEvent, Text } from "pixi.js"
import { Callback, Coordinate, EmberMinimapDot, EmberMinimapExploration, EmberMinimapState, EntityId, TileSize } from "game-common/models"
import { clamp, isMobile } from "../../../../client_util"
import { isAnyTreeEntityTile } from "game-common/map/map"
import { MapSizeButton } from "./map_size_button"
import { HUD_PANEL_FILL, HUD_PANEL_RIM } from "./ui_tokens"

// Frame thickness, matching the follow-cam PiP that shares this HUD slot.
const FRAME = 4
const CORNER = 6

// The three sizes the map comes in, as the longest edge of the map SURFACE in px. The longer room
// axis gets that many pixels and the shorter one shrinks in proportion, so the map always carries the
// room's own aspect ratio -- an ember camp is square, but nothing here assumes that.
//
// "Radar" is the default and deliberately the smallest. What a player checks every few seconds is
// where the red dots are, which a 112px plan answers as well as a 240px one -- and this map floats
// over the game, so every pixel of it is a pixel of camp nobody can see. The bigger two are for
// READING the camp rather than scanning it: counting a wave, finding the corner a forager wandered
// into.
//
// The dots deliberately do NOT scale with these (DOT_R and friends are absolute). Constant-size blips
// on a smaller plan is exactly what makes the small size a radar instead of a tiny unreadable map.
export type MinimapSize = "radar" | "normal" | "large"
export const MINIMAP_SURFACE_MAX: Record<MinimapSize, number> = { radar: 112, normal: 176, large: 240 }
export const MINIMAP_SIZE_ORDER: MinimapSize[] = ["radar", "normal", "large"]
export const DEFAULT_MINIMAP_SIZE: MinimapSize = "radar"

// The next size round the cycle. It WRAPS, which is what lets one button serve three sizes: any size
// is at most two taps away, where a +/- pair would be two controls each dead at one end.
export const nextMinimapSize = (size: MinimapSize): MinimapSize =>
    MINIMAP_SIZE_ORDER[(Math.max(0, MINIMAP_SIZE_ORDER.indexOf(size)) + 1) % MINIMAP_SIZE_ORDER.length]

// Whatever was last written to localStorage, read back as a size THIS build knows -- the stored value
// is just a string someone's browser is holding, and a preset renamed later must not leave the map
// with no size at all.
export const asMinimapSize = (value: unknown): MinimapSize | null =>
    MINIMAP_SIZE_ORDER.includes(value as MinimapSize) ? (value as MinimapSize) : null

// The size control sits inside the map's top-right corner. The MAP chip owns visibility.
const CONTROL_RADIUS = isMobile() ? 15 : 8

// Dot sizes and colours. White = camp member, red = wave attacker, bright green = you (the same
// green the world minimap has always used for the player). Trees are a DIM green wash painted under
// all of them: terrain, not a dot, and far enough from the player's bright green to never be mistaken
// for it.
const DOT_R = 2.2
const PLAYER_DOT_R = 2.8
const MEMBER_COLOR = 0xffffff
const ATTACKER_COLOR = 0xff3b30
const PLAYER_COLOR = 0x39ff6a
const TREE_COLOR = 0x2f6b3d
// Another person in the camp: a player, so player-sized and player-green, but a cooler, dimmer
// green than your own bright one. Near enough to read as "one of us" at a glance, far enough apart
// that you never mistake theirs for the dot you are steering.
const PARTNER_COLOR = 0x2bc4a8
// A stranger at the fire asking to be taken on. Ember gold -- the colour of the thing that drew
// them -- and the ONLY dot on this map that pulses. Nothing else here moves except by travelling,
// so the pulse alone says "this one is new, and it is waiting on you" with no legend to read. It
// also keeps a visitor clear of the red it would otherwise be mistaken for at this dot size: the
// other thing that turns up at your camp uninvited is a wave.
const VISITOR_COLOR = 0xffb648
const VISITOR_PULSE_MS = 1100
// The same stranger, earlier: still crossing the dark towards the fire. The same gold, because it is
// the same creature and nothing has changed about what it wants -- and NOT pulsing, because it is
// not asking yet. So the arrival reads as one continuous thing: a gold dot appears at the edge of
// the map, walks in, and starts to beat when it reaches the fire and puts its hand up.
const APPROACHING_ALPHA = 0.82
// How far the pulse swells the dot. Deliberately modest: the dots already GLIDE a full second
// between snapshots, and a big throb on top of a glide reads as jitter rather than as a beacon.
const VISITOR_PULSE_GROW = 0.55

// How much of the world the map lets through. The BACKDROP, its border and the tree wash are all
// semi-transparent so the map sits lightly over the game rather than punching a panel out of it; the
// dots stay fully opaque, because they are the thing the map is for.
const PANEL_COLOR = HUD_PANEL_FILL
const PANEL_ALPHA = 0.59
const BORDER_ALPHA = 0.36
const TREE_ALPHA = 0.61
const GROUND_COLOR = 0xc3b38b
const GROUND_ALPHA = 0.24
const ROCK_COLOR = 0x9297a3
const ORE_COLOR = 0xb6a482

export interface MinimapTerrainCells {
    trees: Coordinate[]
    rocks: Coordinate[]
    ore: Coordinate[]
    structures: Coordinate[]
}

// Static rock has no entity tile; mineable deposits do. Both come from the same live provider as
// trees, so digging and building update the terrain without another server payload.
export const collectMinimapTerrain = (map: TreeTileSource & {
    isCollisionAt?: (x: number, y: number) => boolean
}, tilesWide: number, tilesHigh: number): MinimapTerrainCells => {
    const cells: MinimapTerrainCells = { trees: [], rocks: [], ore: [], structures: [] }
    for (let x = 0; x < tilesWide; x++) for (let y = 0; y < tilesHigh; y++) {
        const tile = map.getEntityTileAt(x, y)?.entityTile
        const cell = { x, y }
        if (isAnyTreeEntityTile(tile)) cells.trees.push(cell)
        else if (tile && /ore|rock|stone/i.test(tile) && !/wall|floor/i.test(tile)) cells.ore.push(cell)
        else if (tile && /wall|hearth|campfire|chest/i.test(tile)) cells.structures.push(cell)
        else if (map.isCollisionAt?.(x, y)) cells.rocks.push(cell)
    }
    return cells
}

export const decodeMinimapExploration = (exploration: EmberMinimapExploration,
    tilesWide: number, tilesHigh: number): Uint8Array | null => {
    if (exploration.cellTiles !== 2 || exploration.columns !== Math.ceil(tilesWide / 2) ||
        exploration.rows !== Math.ceil(tilesHigh / 2) || !Number.isInteger(exploration.revision) ||
        exploration.revision < 0) return null
    try {
        const decoded = atob(exploration.bits)
        if (decoded.length !== Math.ceil(exploration.columns * exploration.rows / 8)) return null
        return Uint8Array.from(decoded, char => char.charCodeAt(0))
    } catch { return null }
}

// How long a dot takes to travel to a freshly-received position. Matched to the EMBER_MINIMAP
// campaign's broadcast interval, so a dot finishes its glide just as the next snapshot lands -- the
// map moves continuously off a feed that only speaks once a second.
export const DOT_GLIDE_MS = 1000

// One dot's journey between two snapshots. `t` is how far into the glide we are, in ms.
interface DotTrack {
    fromX: number
    fromY: number
    toX: number
    toY: number
    t: number
}

// Where a track is right now. Linear, not eased: the entity really did walk at a steady pace between
// the two samples, and easing would invent an acceleration that never happened.
export const trackPosition = (track: DotTrack): Coordinate => {
    const p = DOT_GLIDE_MS > 0 ? Math.max(0, Math.min(1, track.t / DOT_GLIDE_MS)) : 1
    return {
        x: track.fromX + (track.toX - track.fromX) * p,
        y: track.fromY + (track.toY - track.fromY) * p,
    }
}

// A cell is forest when its entity tile is tagged a tree, grown or young (`tree` in the tile
// definitions; a sapling is untagged -- a seedling is not yet canopy).
// The minimum a tile map has to offer for the tree scan -- so the scan can be tested without standing
// up a whole TileMapMetaProvider. `getEntityTileAt` already accounts for felled trees (a removed
// tile is deleted from the layer map), which is why the scan reads it rather than the raw tile ids.
export interface TreeTileSource {
    getEntityTileAt: (x: number, y: number) => { entityTile?: string } | null | undefined
}

// Every tile cell in the room holding a tree, in TILE coordinates. The client already has the whole
// room's tile map, so trees never have to ride the wire -- only the moving things do.
//
// O(tiles) with a tile-meta lookup per cell, so the caller keys this on the map's collision version
// and re-runs it only when the room's tiles actually change (a felled tree, a placed wall).
export const collectTreeCells = (map: TreeTileSource, tilesWide: number, tilesHigh: number): Coordinate[] => {
    const cells: Coordinate[] = []
    if (!map || !(tilesWide > 0) || !(tilesHigh > 0)) {
        return cells
    }
    for (let x = 0; x < tilesWide; x++) {
        for (let y = 0; y < tilesHigh; y++) {
            if (isAnyTreeEntityTile(map.getEntityTileAt(x, y)?.entityTile)) {
                cells.push({ x, y })
            }
        }
    }
    return cells
}

// Where a dot at fine room coordinate (x, y) lands on a map surface `mapW` x `mapH` px, given a room
// `width` x `height` fine units across. Pure, and returns surface-local px (the caller adds FRAME).
// Clamped so a body that straddles the room's outer edge still shows ON the map rather than under
// the frame.
export const projectDot = (
    dot: Coordinate,
    room: { width: number; height: number },
    mapW: number,
    mapH: number,
): Coordinate => {
    const fx = room.width > 0 ? dot.x / room.width : 0
    const fy = room.height > 0 ? dot.y / room.height : 0
    return {
        x: Math.max(0, Math.min(1, fx)) * mapW,
        y: Math.max(0, Math.min(1, fy)) * mapH,
    }
}

// The map surface size for a room of the given fine extent: the longer axis gets `max` px, the other
// keeps the room's aspect ratio. A degenerate/unknown extent falls back to a square.
export const surfaceSizeFor = (room: { width: number; height: number }, max: number): { w: number; h: number } => {
    if (!(room.width > 0) || !(room.height > 0)) {
        return { w: max, h: max }
    }
    const longest = Math.max(room.width, room.height)
    return {
        w: Math.round((room.width / longest) * max),
        h: Math.round((room.height / longest) * max),
    }
}

// Do two HUD panels share any pixels? Used to decide whether the minimap actually has to step aside
// for whatever has claimed the slot above the roster strip: a map the player has dragged to the other
// side of the screen is in nobody's way, and taking it off screen anyway is what taught at least one
// player that the map could not simply be left open.
//
// Touching edges do not count as overlapping -- two panels flush against each other are both fully
// visible.
export interface PanelRect {
    x: number
    y: number
    width: number
    height: number
}

export const rectsOverlap = (a: PanelRect, b: PanelRect): boolean =>
    a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height

// Keep a free-floating HUD panel wholly inside `bounds`, in whatever coordinates the owner works in.
// Pure: WHICH edges a panel may touch is the owner's business, but the arithmetic is not. The max()
// guards a panel bigger than the space it is in, which would otherwise hand clamp() a range with its
// ends the wrong way round.
export const clampPanelWithin = (
    at: Coordinate,
    panel: { width: number; height: number },
    bounds: { left: number; top: number; right: number; bottom: number },
): Coordinate => ({
    x: clamp(at.x, bounds.left, Math.max(bounds.left, bounds.right - panel.width)),
    y: clamp(at.y, bounds.top, Math.max(bounds.top, bounds.bottom - panel.height)),
})

// What a panel being dragged asks of its owner. The widget knows where the pointer has taken it; the
// OWNER owns HUD geometry -- and is what re-anchors the panel above the roster strip, so it would
// otherwise snatch the map back mid-drag -- which is why it does the clamping and the actual move.
export interface EmberMinimapDragHandlers {
    // The press landed on the panel, before any movement. Lets the owner hold off the world's
    // click-to-attack for the length of the press.
    onGrab: Callback
    // Where the player is asking the panel to go, in the panel's PARENT coordinates.
    onDragTo: (x: number, y: number) => void
    // The press ended -- which the pointer may well have done somewhere else entirely.
    onRelease: Callback
}

// The ember room's minimap: a framed scale plan of the whole camp room, showing the forest it sits in
// (dim green), a dot for every camp member (white) and every live wave attacker (red), plus the
// player (bright green). It takes the HUD slot directly above the roster strip -- the same slot as
// the follow-cam PiP, which wins it whenever the player is actively tracking a roster member (see
// GuiManager.positionEmberMinimap).
//
// The MOVING things are fed by the server (Protocol.type_updateEmberMinimap) rather than read off the
// client's own entities, because the whole point is the attacker crossing the far side of the room --
// which the world-state sync only refreshes about once a second at that distance. The trees are the
// opposite case: the client already holds the room's entire tile map, so they are scanned locally and
// never sent.
//
// Four layers, so the cheap thing happens often and the costly thing happens rarely:
//   frame     drawn only when the room's extent changes
//   trees     drawn only when the room's TILES change (felled tree, placed wall) -- the big one
//   dots      redrawn each frame, but ONLY while something is actually gliding (see animate)
//   playerDot drawn once, then just REPOSITIONED every frame (no geometry churn)
//
// The panel is DRAGGABLE: the corner of the camp it covers is sometimes the corner the player needs to
// watch, so the whole map is its own grab handle (nothing on it is clickable but the size
// control) and it can be parked anywhere on screen. See EmberMinimapDragHandlers for the division of
// labour with GuiManager.
//
// It is also RESIZABLE, between three presets (see MINIMAP_SURFACE_MAX). The widget owns the cycle --
// it is the thing that knows the presets -- and only reports the size it landed on, so GuiManager can
// remember the choice and re-clamp the panel it has just changed the size of.
export class EmberMinimapWidget extends Container {
    private frame = new Graphics()
    private caveContents = new Container()
    private sightMask = new Graphics()
    private sightPolygon: Coordinate[] | null = null
    private sightRoomId?: string
    private rememberedSightPolygon: Coordinate[] | null = null
    private rememberedSightKey = ""
    private caveDiscovery = new Map<string, { columns: number; rows: number; bits: Uint8Array }>()
    private terrainLayer = new Container()
    private ground = new Graphics()
    private terrainMask = new Graphics()
    private trees = new Graphics()
    private rocks = new Graphics()
    private terrainCells: MinimapTerrainCells = { trees: [], rocks: [], ore: [], structures: [] }
    private discovery: EmberMinimapExploration | null = null
    private discoveryBits: Uint8Array | null = null
    // A bounded, disposable mirror of server snapshots, including packets that arrive just before
    // roomUpdated. Clearing the active room must not lose the only bitmap in an unchanged feed.
    private discoveryCache = new Map<string, EmberMinimapExploration>()
    private discoveryKey = ""
    private expectedRoomId: string | null = null
    private dots = new Graphics()
    // Outside the cave sight mask so the dev command can locate still-hidden rooms.
    private voidRooms = new Graphics()
    private bonecallerLabel = new Text("", { fontSize: 10, fill: 0xe9b4ff, stroke: 0x000000, strokeThickness: 3 })
    private mineLabel = new Text("Mine", { fontSize: 10, fill: 0xf0cc83, stroke: 0x000000, strokeThickness: 3 })
    private rescueLabel = new Text("", { fontSize: 10, fill: 0xffdd88, stroke: 0x000000, strokeThickness: 3 })
    private rescueTracks: Map<EntityId, DotTrack> = new Map()
    private playerDot = new Graphics()
    private state: EmberMinimapState | null = null
    private size: MinimapSize = DEFAULT_MINIMAP_SIZE
    private mapW = MINIMAP_SURFACE_MAX[DEFAULT_MINIMAP_SIZE]
    private mapH = MINIMAP_SURFACE_MAX[DEFAULT_MINIMAP_SIZE]
    // The last tree scan, kept so the layer can be repainted if the map's extent changes under it.
    private treeCells: Coordinate[] = []
    private treeTilesWide = 0
    private treeTilesHigh = 0
    // Where each dot is travelling, keyed by its entity id -- the whole reason the dots carry one.
    // Kept per kind because a camper never becomes an attacker, and it saves storing the colour.
    private memberTracks: Map<EntityId, DotTrack> = new Map()
    private attackerTracks: Map<EntityId, DotTrack> = new Map()
    // The other PEOPLE in the camp. Glided like the rest -- they come off the same once-a-second
    // feed -- and kept apart from `members` so they can be drawn last, bigger and in their own
    // green. The viewer is never in here; the server leaves them out, and their own dot is
    // `playerDot`, repositioned every frame off the entity the client already tracks.
    private partnerTracks: Map<EntityId, DotTrack> = new Map()
    // Strangers at the fire. Tracked exactly like everyone else (so their dot glides rather than
    // teleports between snapshots); what is different is only how it is drawn.
    private visitorTracks: Map<EntityId, DotTrack> = new Map()
    // Strangers still on their way to it. A separate map rather than a flag on the visitor track,
    // so each kind is plotted by one `plot` call with its own treatment. The same id moves from one
    // map to the other when the recruit reaches the fire; its glide restarts there, which costs
    // nothing visible because by then it has stopped walking.
    private approachingTracks: Map<EntityId, DotTrack> = new Map()
    // Free-running phase for the visitor pulse, so two of them beat together rather than each on
    // its own clock.
    private visitorPulseMs = 0
    // What the frame was last drawn for -- the room's extent AND the chosen size, since either one
    // changes the surface. Keyed so a steady stream of broadcasts doesn't redraw it.
    private drawnExtent = ""
    private drawnDiscoveryKey = ""

    // Whether the pointer currently has hold of the panel, and where it took hold: the panel's origin
    // MINUS the pointer, in PARENT coordinates. Fixed at pointerdown rather than recomputed per frame,
    // or a drag that ran into a screen edge and came back would leave the panel creeping out from
    // under the cursor.
    private grabbed = false
    private grabDx = 0
    private grabDy = 0
    private drag?: EmberMinimapDragHandlers

    private sizeButton: MapSizeButton
    private onSizeChange?: (size: MinimapSize) => void

    // The owner moves the panel and remembers size changes.
    constructor(drag?: EmberMinimapDragHandlers, onSizeChange?: (size: MinimapSize) => void) {
        super()
        this.onSizeChange = onSizeChange
        this.terrainLayer.addChild(this.ground, this.trees, this.rocks)
        this.caveContents.addChild(this.terrainLayer, this.terrainMask, this.dots, this.playerDot,
            this.rescueLabel, this.bonecallerLabel, this.mineLabel)
        this.addChild(this.frame, this.caveContents, this.sightMask, this.voidRooms)
        this.sizeButton = new MapSizeButton({ radius: CONTROL_RADIUS, onClick: this.cycleSize })
        this.addChild(this.sizeButton)
        this.drawPlayerDot()
        this.drawFrame()
        this.attachDrag(drag)
        this.visible = false
    }

    // Make the whole panel a grab handle. Deliberately NO hitArea of its own: the frame graphic
    // already IS the panel's shape, and pixi skips a container's children entirely once the pointer
    // falls outside an explicit hitArea -- which would strand the size control in its hover state
    // whenever the pointer left the map in one jump.
    //
    // pointermove/pointerup are bound here rather than on some capture layer because pixi dispatches
    // both to every interactive object regardless of what was hit (moveWhenInside is off), so a drag
    // keeps its stream of moves even while the pointer is way off the panel -- which it will be the
    // moment the panel is dragged up against a screen edge.
    private attachDrag = (drag?: EmberMinimapDragHandlers) => {
        this.drag = drag
        this.interactive = true
        this.cursor = "grab"
        this.on("pointerdown", this.onGrab)
        this.on("pointermove", this.onDragMove)
        this.on("pointerup", this.onRelease)
        this.on("pointerupoutside", this.onRelease)
    }

    // A press on the panel takes hold of it -- unless it landed on the size control, the
    // only things up here that are not a grab handle. (pixi bubbles a child's pointerdown to its
    // parent, and `target` is the deepest INTERACTIVE object that was actually hit, so that is what
    // tells them apart.)
    private onGrab = (event: InteractionEvent) => {
        if (event.target !== this || !this.parent) {
            return
        }
        const pointer = event.data.getLocalPosition(this.parent)
        this.grabDx = this.x - pointer.x
        this.grabDy = this.y - pointer.y
        this.grabbed = true
        this.cursor = "grabbing"
        this.drag?.onGrab()
    }

    private onDragMove = (event: InteractionEvent) => {
        if (!this.grabbed || !this.parent) {
            return
        }
        const pointer = event.data.getLocalPosition(this.parent)
        this.drag?.onDragTo(pointer.x + this.grabDx, pointer.y + this.grabDy)
    }

    private onRelease = () => {
        if (!this.grabbed) {
            return
        }
        this.grabbed = false
        this.cursor = "grab"
        this.drag?.onRelease()
    }

    // Keep the size control inside the map, clear of the screen edge.
    private positionControls = () => {
        this.sizeButton.x = this.getWidth() - CONTROL_RADIUS - 3
        this.sizeButton.y = CONTROL_RADIUS + 3
    }

    private drawFrame = () => {
        this.frame.clear()
        this.frame.beginFill(PANEL_COLOR, PANEL_ALPHA)
        this.frame.lineStyle(2, HUD_PANEL_RIM, BORDER_ALPHA)
        this.frame.drawRoundedRect(0, 0, this.mapW + FRAME * 2, this.mapH + FRAME * 2, CORNER)
        this.frame.endFill()
        this.positionControls()
    }

    private drawPlayerDot = () => {
        this.playerDot.clear()
        this.playerDot.beginFill(PLAYER_COLOR, 1)
        this.playerDot.drawCircle(0, 0, PLAYER_DOT_R)
        this.playerDot.endFill()
        this.playerDot.visible = false
    }

    // One cell-sized square per tree tile. Rects rather than circles because a forest is hundreds of
    // cells and a square is 2 triangles to a circle's dozens — and at ~3.5px a tile, a square IS the
    // honest shape for "this cell has canopy in it".
    private drawTrees = () => {
        this.trees.clear()
        if (this.treeCells.length < 1 || !(this.treeTilesWide > 0) || !(this.treeTilesHigh > 0)) {
            return
        }
        const cellW = this.mapW / this.treeTilesWide
        const cellH = this.mapH / this.treeTilesHigh
        // Overdrawn by a hair so neighbouring cells of one canopy read as a clump, not a grid.
        const w = cellW + 0.5
        const h = cellH + 0.5
        this.trees.beginFill(TREE_COLOR, TREE_ALPHA)
        for (const cell of this.treeCells) {
            this.trees.drawRect(FRAME + cell.x * cellW, FRAME + cell.y * cellH, w, h)
        }
        this.trees.endFill()
    }

    // Rasterize the world's existing polygon into a bounded, run-scoped discovery bitmap.
    // Four-pixel cells preserve wall edges without building occluders or casting more rays.
    updateSight = (roomId: string | undefined, polygon: Coordinate[] | null) => {
        if (this.sightRoomId === roomId && this.sightPolygon === polygon) return
        this.sightRoomId = roomId
        this.sightPolygon = polygon
        if (this.rememberSight()) this.drawSight()
    }

    private rememberSight = (): boolean => {
        if (this.state?.terrain !== "cave" || this.state.roomId !== this.sightRoomId ||
            !this.discoveryKey || !this.sightPolygon?.length) return false
        if (this.rememberedSightKey === this.discoveryKey &&
            this.rememberedSightPolygon === this.sightPolygon) return false
        this.rememberedSightKey = this.discoveryKey
        this.rememberedSightPolygon = this.sightPolygon
        const columns = Math.ceil(this.state.width / 4), rows = Math.ceil(this.state.height / 4)
        let seen = this.caveDiscovery.get(this.discoveryKey)
        if (!seen) {
            seen = { columns, rows, bits: new Uint8Array(columns * rows) }
            this.caveDiscovery.set(this.discoveryKey, seen)
            if (this.caveDiscovery.size > 4) this.caveDiscovery.delete(this.caveDiscovery.keys().next().value)
        }
        let changed = false
        const polygon = this.sightPolygon
        const minY = Math.max(0, Math.floor(Math.min(...polygon.map(point => point.y)) / 4))
        const maxY = Math.min(rows - 1, Math.floor(Math.max(...polygon.map(point => point.y)) / 4))
        for (let y = minY; y <= maxY; y++) {
            const py = y * 4 + 2, crossings: number[] = []
            for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
                const a = polygon[j], b = polygon[i]
                if ((a.y > py) !== (b.y > py)) {
                    crossings.push(a.x + (py - a.y) * (b.x - a.x) / (b.y - a.y))
                }
            }
            crossings.sort((a, b) => a - b)
            for (let i = 0; i + 1 < crossings.length; i += 2) {
                const start = Math.max(0, Math.ceil((crossings[i] - 2) / 4))
                const end = Math.min(columns, Math.ceil((crossings[i + 1] - 2) / 4))
                for (let x = start; x < end; x++) {
                    const index = y * columns + x
                    if (!seen.bits[index]) { seen.bits[index] = 1; changed = true }
                }
            }
        }
        return changed
    }

    private drawSight = () => {
        this.sightMask.clear()
        this.caveContents.mask = this.state?.terrain === "cave" ? this.sightMask : null
        const seen = this.caveDiscovery.get(this.discoveryKey)
        if (this.state?.terrain !== "cave" || !seen) return
        const cellW = 4 * this.mapW / this.state.width, cellH = 4 * this.mapH / this.state.height
        this.sightMask.beginFill(0xffffff)
        for (let y = 0; y < seen.rows; y++) {
            let start = -1
            for (let x = 0; x <= seen.columns; x++) {
                const known = x < seen.columns && seen.bits[y * seen.columns + x]
                if (known && start < 0) start = x
                if (!known && start >= 0) {
                    this.sightMask.drawRect(FRAME + start * cellW, FRAME + y * cellH,
                        Math.min(this.mapW, x * cellW) - start * cellW,
                        Math.min(cellH, this.mapH - y * cellH))
                    start = -1
                }
            }
        }
        this.sightMask.endFill()
    }

    private drawDiscovery = () => {
        this.ground.clear()
        this.terrainMask.clear()
        // Legacy snapshots have no identity and keep their existing full forest treatment.
        this.terrainLayer.mask = this.state?.generationId && this.state.terrain !== "cave" ? this.terrainMask : null
        // Pixi makes a detached mask renderable again. In caves this graphic is unused,
        // so leave it empty rather than painting the server's white discovery bitmap.
        if (this.state?.terrain === "cave") {
            this.ground.beginFill(GROUND_COLOR, GROUND_ALPHA)
                .drawRect(FRAME, FRAME, this.mapW, this.mapH).endFill()
            return
        }
        if (!this.discovery || !this.discoveryBits) return
        const { columns, rows, cellTiles } = this.discovery
        const cellW = this.mapW * cellTiles / this.tilesWide()
        const cellH = this.mapH * cellTiles / this.tilesHigh()
        this.terrainMask.beginFill(0xffffff)
        // Merge each row into runs. Circular reveal stamps make a walked frontier; tiny rounded
        // corners soften its coarse cells without filters, textures or per-frame terrain rebuilds.
        for (let y = 0; y < rows; y++) {
            let start = -1
            for (let x = 0; x <= columns; x++) {
                const index = y * columns + x
                const known = x < columns && !!(this.discoveryBits[index >> 3] & (1 << (index & 7)))
                if (known && start < 0) start = x
                if (!known && start >= 0) {
                    const left = start * cellW, top = y * cellH
                    const width = Math.min(this.mapW, x * cellW) - left
                    const height = Math.min(cellH, this.mapH - top)
                    this.terrainMask.drawRoundedRect(FRAME + left, FRAME + top, width, height, 0.6)
                    start = -1
                }
            }
        }
        this.terrainMask.endFill()
        this.ground.beginFill(GROUND_COLOR, GROUND_ALPHA)
            .drawRect(FRAME, FRAME, this.mapW, this.mapH).endFill()
    }

    private drawRockAndStructures = () => {
        this.rocks.clear()
        if (!(this.treeTilesWide > 0) || !(this.treeTilesHigh > 0)) return
        const cellW = this.mapW / this.treeTilesWide, cellH = this.mapH / this.treeTilesHigh
        const paint = (cells: Coordinate[], color: number, alpha: number) => {
            this.rocks.beginFill(color, alpha)
            cells.forEach(cell => this.rocks.drawRect(FRAME + cell.x * cellW,
                FRAME + cell.y * cellH, cellW + 0.25, cellH + 0.25))
            this.rocks.endFill()
        }
        if (this.state?.terrain === "cave") {
            paint(this.terrainCells.rocks, ROCK_COLOR, 0.75)
            paint(this.terrainCells.ore, ORE_COLOR, 0.8)
        }
        paint(this.terrainCells.structures, 0xc5bba1, 0.8)
    }

    private plot = (tracks: Map<EntityId, DotTrack>, color: number, radius: number = DOT_R, alpha: number = 1) => {
        if (tracks.size < 1) {
            return
        }
        this.dots.beginFill(color, alpha)
        tracks.forEach(track => {
            const p = projectDot(trackPosition(track), this.state!, this.mapW, this.mapH)
            this.dots.drawCircle(FRAME + p.x, FRAME + p.y, radius)
        })
        this.dots.endFill()
    }

    private drawDots = () => {
        this.dots.clear()
        this.rescueLabel.visible = !!this.state?.rescue
        this.bonecallerLabel.visible = !!this.state?.bonecaller
        this.mineLabel.visible = !!this.state?.mine
        if (!this.state) {
            return
        }
        // Attackers last so an attacker standing on a defender still reads as the threat -- but
        // partners after even them: a person is never the thing you want hidden under a dot, and an
        // attacker ON a partner is read off the red crowding them rather than off the green going
        // missing.
        this.plot(this.memberTracks, MEMBER_COLOR)
        this.plot(this.attackerTracks, ATTACKER_COLOR)
        // Under the people: somebody walking in is news, but it is not yet a question, and it
        // crosses open ground where there is nothing to be hidden under anyway.
        this.plot(this.approachingTracks, VISITOR_COLOR, DOT_R, APPROACHING_ALPHA)
        this.plot(this.partnerTracks, PARTNER_COLOR, PLAYER_DOT_R)
        // Over everything, including a partner: a visitor is the one dot on this map that is a
        // question being asked of the player, and a question must never be the thing hidden under
        // somebody walking past it.
        if (this.visitorTracks.size > 0) {
            // 0 -> 1 -> 0 over the period, as one shared phase so two visitors beat together.
            const beat = 0.5 - 0.5 * Math.cos((this.visitorPulseMs / VISITOR_PULSE_MS) * Math.PI * 2)
            this.plot(this.visitorTracks, VISITOR_COLOR, DOT_R * (1 + VISITOR_PULSE_GROW * beat), 0.72 + 0.28 * beat)
        }
        const rescue = this.state.rescue
        if (rescue) {
            this.plot(this.rescueTracks, rescue.state === "In danger" ? ATTACKER_COLOR : VISITOR_COLOR, DOT_R + 2)
            const p = projectDot(rescue, this.state, this.mapW, this.mapH)
            this.rescueLabel.text = rescue.state === "Waiting" ? "survivor" : rescue.state
            this.rescueLabel.x = Math.max(FRAME, Math.min(FRAME + p.x + 5, this.mapW - this.rescueLabel.width))
            this.rescueLabel.y = FRAME + p.y - 14
        }
        const mine = this.state.mine
        if (mine) {
            const p = projectDot(mine, this.state, this.mapW, this.mapH)
            const x = FRAME + p.x, y = FRAME + p.y
            this.dots.lineStyle(1.5, 0xf0cc83).moveTo(x - 4, y + 3)
                .lineTo(x - 4, y).lineTo(x, y - 4).lineTo(x + 4, y).lineTo(x + 4, y + 3)
            this.dots.lineStyle(0)
            this.mineLabel.x = Math.max(FRAME, Math.min(x + 6, this.mapW - this.mineLabel.width))
            this.mineLabel.y = Math.max(FRAME, y - 15)
        }
        const ritual = this.state.bonecaller
        if (ritual) {
            const p = projectDot(ritual, this.state, this.mapW, this.mapH)
            const x = FRAME + p.x, y = FRAME + p.y
            const active = ritual.state === "active"
            const beat = 0.5 - 0.5 * Math.cos(this.visitorPulseMs / VISITOR_PULSE_MS * Math.PI * 2)
            const radius = active ? 4 + beat * 1.5 : 3
            this.dots.lineStyle(1.5, active ? 0xd981ff : 0x9b8aa4, active ? 0.7 + 0.3 * beat : 0.7)
                .moveTo(x, y - radius).lineTo(x + radius, y).lineTo(x, y + radius)
                .lineTo(x - radius, y).lineTo(x, y - radius)
            this.dots.lineStyle(0)
            if (active) for (let stone = 0; stone < 3; stone++) {
                this.dots.beginFill(stone < ritual.stage ? 0xd981ff : 0x62596a)
                    .drawCircle(x + (stone - 1) * 4, y + 9, 1.5).endFill()
            }
            this.bonecallerLabel.text = active ? "ritual: wave 8" :
                ritual.state === "interrupted" ? "ritual stopped" : "dead risen"
            this.bonecallerLabel.x = Math.max(FRAME, Math.min(x + 6, this.mapW - this.bonecallerLabel.width))
            this.bonecallerLabel.y = Math.max(FRAME, y - 15)
        }

    }

    private drawVoidRooms = () => {
        this.voidRooms.clear()
        if (this.state?.terrain !== "cave" || !(this.state.width > 0) || !(this.state.height > 0)) return
        const scaleX = this.mapW / this.state.width, scaleY = this.mapH / this.state.height
        this.voidRooms.lineStyle(1, 0xff3333, 1).beginFill(0xff3333, 0.5)
        for (const room of this.state.voidRooms || []) {
            this.voidRooms.drawRect(FRAME + room.x * scaleX, FRAME + room.y * scaleY,
                room.width * scaleX, room.height * scaleY)
        }
        this.voidRooms.endFill()
    }

    // Fold one snapshot's dots into their tracks: a dot seen before is re-aimed FROM wherever it has
    // glided to so far (never from its last reported position, which would snap it back), a dot seen
    // for the first time simply appears where it is, and a dot that has gone is dropped.
    private syncTracks = (tracks: Map<EntityId, DotTrack>, dots: EmberMinimapDot[]) => {
        const present = new Set<EntityId>()
        for (const dot of dots || []) {
            if (!dot || dot.id === undefined) {
                continue
            }
            present.add(dot.id)
            const track = tracks.get(dot.id)
            if (!track) {
                tracks.set(dot.id, { fromX: dot.x, fromY: dot.y, toX: dot.x, toY: dot.y, t: DOT_GLIDE_MS })
                continue
            }
            if (track.toX === dot.x && track.toY === dot.y) {
                // Standing still (or still on its way to this very spot) -- leave the glide alone
                // rather than restarting it, which would stall a dot that hasn't arrived yet.
                continue
            }
            const at = trackPosition(track)
            track.fromX = at.x
            track.fromY = at.y
            track.toX = dot.x
            track.toY = dot.y
            track.t = 0
        }
        for (const id of Array.from(tracks.keys())) {
            if (!present.has(id)) {
                tracks.delete(id)
            }
        }
    }

    // Advance every glide by a frame, and repaint the dots only if any of them actually moved. A camp
    // standing still costs nothing.
    animate = (dtMs: number) => {
        if (!this.state) {
            return
        }
        let moved = false
        const advance = (tracks: Map<EntityId, DotTrack>) =>
            tracks.forEach(track => {
                if (track.t < DOT_GLIDE_MS) {
                    track.t = Math.min(DOT_GLIDE_MS, track.t + dtMs)
                    moved = true
                }
            })
        advance(this.memberTracks)
        advance(this.attackerTracks)
        advance(this.partnerTracks)
        advance(this.visitorTracks)
        advance(this.approachingTracks)
        advance(this.rescueTracks)
        // A visitor's dot breathes whether or not anybody moved, so its presence is what makes this
        // map repaint every frame. That is the only case: with nobody at the fire -- which is the
        // camp almost all of the time -- a still camp still costs nothing.
        if (this.visitorTracks.size > 0 || this.state.bonecaller?.state === "active") {
            this.visitorPulseMs = (this.visitorPulseMs + dtMs) % VISITOR_PULSE_MS
            moved = true
        }
        if (moved) {
            this.drawDots()
        }
    }

    // What the drawn surface depends on: the room's shape and the chosen size. Compared against
    // `drawnExtent` so a snapshot a second does no geometry work, while a resize or a new room does.
    private surfaceKey = (): string =>
        this.state ? `${this.state.width}x${this.state.height}@${this.size}` : `@${this.size}`

    // Re-measure the surface and repaint everything laid out in its pixels. Called on a new room
    // shape and on a resize -- the two things that move every pixel on this panel.
    private applySurface = () => {
        const surface = surfaceSizeFor(this.state ?? { width: 0, height: 0 }, MINIMAP_SURFACE_MAX[this.size])
        this.mapW = surface.w
        this.mapH = surface.h
        this.drawnExtent = this.surfaceKey()
        this.drawnDiscoveryKey = this.discoveryKey
        this.drawFrame()
        // Both of these are laid out in the surface's px, so they have to follow it. (The player's own
        // dot needs no repaint -- it is a fixed circle that trackPlayer re-positions every frame.)
        this.drawDiscovery()
        this.drawSight()
        this.drawTrees()
        this.drawRockAndStructures()
        this.drawDots()
        this.drawVoidRooms()
    }

    // Step to the next size and tell the owner, which is what remembers the choice and re-clamps the
    // panel -- it has just changed size, so the spot it was parked in may no longer hold it.
    private cycleSize = () => {
        this.setSize(nextMinimapSize(this.size))
        this.onSizeChange?.(this.size)
    }

    // Put the map at a given size. GuiManager calls this once at startup with the remembered choice,
    // and the corner control calls it through cycleSize.
    setSize = (size: MinimapSize) => {
        if (size === this.size) {
            return
        }
        this.size = size
        this.applySurface()
    }

    // Take the latest snapshot (null clears the map, e.g. on leaving the room).
    update = (state: EmberMinimapState | null) => {
        const key = state?.generationId ? `${state.roomId}:${state.runId}:${state.generationId}` : ""
        if (state?.roomId && this.expectedRoomId && state.roomId !== this.expectedRoomId) {
            // A new-room feed may precede the room packet. Keep its discovery, but never draw it
            // using the old room's terrain. The next feed can restore it even without a bitmap.
            if (key && state.exploration && decodeMinimapExploration(state.exploration,
                Math.round(state.width / TileSize), Math.round(state.height / TileSize))) {
                this.discoveryCache.delete(key)
                this.discoveryCache.set(key, state.exploration)
                if (this.discoveryCache.size > 4) this.discoveryCache.delete(this.discoveryCache.keys().next().value)
            }
            return
        }
        if (key !== this.discoveryKey) {
            this.discoveryKey = key
            this.discovery = key ? this.discoveryCache.get(key) || null : null
            this.discoveryBits = null
            this.treeCells = []
            this.terrainCells = { trees: [], rocks: [], ore: [], structures: [] }
            this.memberTracks.clear()
            this.attackerTracks.clear()
            this.partnerTracks.clear()
            this.visitorTracks.clear()
            this.approachingTracks.clear()
            this.rescueTracks.clear()
            this.playerDot.visible = false
        }
        const previousDiscovery = this.discovery
        this.state = state
        this.drawVoidRooms()
        this.rememberSight()
        this.drawSight()
        if (!state) {
            this.dots.clear()
            this.trees.clear()
            this.rocks.clear()
            this.ground.clear()
            this.terrainMask.clear()
            this.discovery = null
            this.discoveryBits = null
            this.terrainCells = { trees: [], rocks: [], ore: [], structures: [] }
            this.treeCells = []
            this.treeTilesWide = 0
            this.treeTilesHigh = 0
            this.memberTracks.clear()
            this.attackerTracks.clear()
            this.partnerTracks.clear()
            this.visitorTracks.clear()
            this.approachingTracks.clear()
            this.rescueTracks.clear()
            this.rescueLabel.visible = false
            this.bonecallerLabel.visible = false
            this.mineLabel.visible = false
            this.playerDot.visible = false
            return
        }

        if (state.exploration && (!this.discovery || state.exploration.revision >= this.discovery.revision)) {
            const bits = decodeMinimapExploration(state.exploration, this.tilesWide(), this.tilesHigh())
            if (bits) {
                this.discovery = state.exploration
                this.discoveryBits = bits
                if (key) {
                    this.discoveryCache.delete(key)
                    this.discoveryCache.set(key, state.exploration)
                    if (this.discoveryCache.size > 4) this.discoveryCache.delete(this.discoveryCache.keys().next().value)
                }
            }
        }
        if (this.discovery && !this.discoveryBits) {
            this.discoveryBits = decodeMinimapExploration(this.discovery, this.tilesWide(), this.tilesHigh())
        }
        if (key !== "" && (!previousDiscovery || this.discovery !== previousDiscovery)) this.drawDiscovery()
        if (this.surfaceKey() !== this.drawnExtent || key !== this.drawnDiscoveryKey) {
            this.applySurface()
        }

        this.syncTracks(this.memberTracks, state.members)
        this.syncTracks(this.attackerTracks, state.attackers)
        // Absent on an older server's snapshot; syncTracks reads a missing list as an empty one,
        // which correctly empties the partner tracks rather than freezing the last dots in place.
        this.syncTracks(this.partnerTracks, state.players)
        // Missing from an older server's snapshot; an absent list is an empty one, which correctly
        // empties the tracks rather than leaving a gold dot pulsing over nobody.
        this.syncTracks(this.visitorTracks, state.visitors)
        this.syncTracks(this.approachingTracks, state.approaching)
        this.syncTracks(this.rescueTracks, state.rescue ? [state.rescue] : [])
        this.drawDots()
    }

    selectRoom = (roomId: string) => {
        this.expectedRoomId = roomId
        this.update(null)
    }

    // Replace the forest. `cells` are TILE coordinates in a room `tilesWide` x `tilesHigh` tiles; the
    // caller (GuiManager) scans them off the client's own tile map and only when it has changed.
    updateTrees = (cells: Coordinate[], tilesWide: number, tilesHigh: number) => {
        this.treeCells = cells || []
        this.treeTilesWide = tilesWide
        this.treeTilesHigh = tilesHigh
        this.drawTrees()
    }

    updateTerrain = (cells: MinimapTerrainCells, tilesWide: number, tilesHigh: number) => {
        this.terrainCells = cells
        this.updateTrees(cells.trees, tilesWide, tilesHigh)
        this.drawRockAndStructures()
    }

    // Move the player's own dot. Called every frame with the client's live player location, which is
    // predicted locally and so smoother than anything the broadcast could carry.
    trackPlayer = (location?: Coordinate) => {
        if (!this.state || !location) {
            this.playerDot.visible = false
            return
        }
        const p = projectDot(location, this.state, this.mapW, this.mapH)
        this.playerDot.position.set(FRAME + p.x, FRAME + p.y)
        this.playerDot.visible = true
    }

    // Whether there is a room to draw at all -- GuiManager keeps the widget off screen until the
    // first broadcast arrives, and takes it down again on leaving the room.
    hasData = (): boolean => !!this.state

    // The room's extent in TILES, for the caller's tree scan. Zero until the first broadcast.
    tilesWide = (): number => (this.state ? Math.round(this.state.width / TileSize) : 0)
    tilesHigh = (): number => (this.state ? Math.round(this.state.height / TileSize) : 0)

    // Outer frame dimensions, for positioning against the rest of the HUD.
    getWidth = (): number => this.mapW + FRAME * 2
    getHeight = (): number => this.mapH + FRAME * 2
    getDefaultHeight = (): number =>
        surfaceSizeFor(this.state ?? { width: 0, height: 0 }, MINIMAP_SURFACE_MAX[DEFAULT_MINIMAP_SIZE]).h + FRAME * 2

    // Test seam: the corner control that steps the map through its sizes.
    sizeControl = (): MapSizeButton => this.sizeButton

    // The size the map is currently at.
    currentSize = (): MinimapSize => this.size

    // Test seam: whether the pointer currently has hold of the panel.
    isGrabbed = (): boolean => this.grabbed

    // Test seams: what the map is currently showing.
    memberCount = (): number => this.memberTracks.size
    attackerCount = (): number => this.attackerTracks.size
    visitorCount = (): number => this.visitorTracks.size
    approachingCount = (): number => this.approachingTracks.size
    // Where a dot is being drawn right now, in ROOM (fine) coordinates -- i.e. part-way through its
    // glide, not where the last snapshot put it.
    dotPosition = (id: EntityId): Coordinate | null => {
        const track =
            this.memberTracks.get(id) ||
            this.attackerTracks.get(id) ||
            this.partnerTracks.get(id) ||
            this.visitorTracks.get(id) ||
            this.approachingTracks.get(id)
        return track ? trackPosition(track) : null
    }
    treeCount = (): number => this.treeCells.length
    isTerrainRevealed = (x: number, y: number): boolean => {
        if (this.state?.terrain === "cave") {
            const seen = this.caveDiscovery.get(this.discoveryKey)
            if (!seen || x < 0 || y < 0 || x >= this.tilesWide() || y >= this.tilesHigh()) return false
            return !!seen.bits[Math.floor((y + 0.5) * TileSize / 4) * seen.columns +
                Math.floor((x + 0.5) * TileSize / 4)]
        }
        if (!this.state?.generationId) return true
        if (!this.discovery || !this.discoveryBits || x < 0 || y < 0 ||
            x >= this.tilesWide() || y >= this.tilesHigh()) return false
        const index = Math.floor(y / this.discovery.cellTiles) * this.discovery.columns +
            Math.floor(x / this.discovery.cellTiles)
        return !!(this.discoveryBits[index >> 3] & (1 << (index & 7)))
    }
    isTrackingPlayer = (): boolean => this.playerDot.visible
    playerDotPosition = (): Coordinate => ({ x: this.playerDot.x, y: this.playerDot.y })
}
